"use client";

import { IntlProvider, type ResolvedIntlConfig } from "react-intl";
import { SWRConfig } from "swr";
import { FloatingSave } from "./components/Form/Fields/FloatingSave";
import { ProductionType } from "./components/Form/Steps/ProductionType";
import { ShapeDimensions } from "./components/Form/Steps/ShapeDimensions";
import { Thickness } from "./components/Form/Steps/Thickness";
import { OutgoingDataResolver } from "./components/OutgoingDataResolver";
import { Root as StepRoot } from "./components/Steps/Root";
import { AddToCartContextProvider } from "./context/AddToCartProvider";
import { CardContextProvider } from "./context/CardProvider";
import type { ConfigContextValue } from "./context/ConfigContext";
import { ConfigContextProvider } from "./context/ConfigProvider";
import { ErrorContextProvider } from "./context/ErrorProvider";
import { PostDataContextProvider } from "./context/PostDataProvider";
import { PriceContextProvider } from "./context/PriceProvider";

export interface PanelConfiguratorProps extends ConfigContextValue {
	messages?: ResolvedIntlConfig["messages"];
	lang: string;
	apiToken?: string;
	cartId?: string;
}

type FetcherArgs = Parameters<typeof fetch>;

export function PanelConfiguratorComponent(props: PanelConfiguratorProps) {
	const { lang, messages = {}, apiToken, ...config } = props;

	return (
		<IntlProvider locale={lang} messages={messages} onError={() => null}>
			<ConfigContextProvider config={{ ...config }}>
				<SWRConfig
					value={{
						fetcher: async (...args: FetcherArgs) => {
							const [url, options] = args;

							const headers = options?.headers ?? ({} as HeadersInit);

							if (apiToken) {
								Object.assign(headers, { "X-API-Key": apiToken });
							}

							if (props.cartId) {
								Object.assign(headers, { "X-Cart-Id": props.cartId });
							}

							const response = await fetch(url, {
								...options,
								headers,
							});

							if (!response.ok) throw new Error(response.statusText);

							return response.json();
						},
					}}
				>
					<CardContextProvider>
						<PostDataContextProvider>
							<PriceContextProvider>
								<ErrorContextProvider>
									<AddToCartContextProvider>
										<OutgoingDataResolver />
										<FloatingSave />
										<StepRoot>
											<Thickness />
											<ProductionType />
											<ShapeDimensions />
											{/* <Finishes /> */}
										</StepRoot>
									</AddToCartContextProvider>
								</ErrorContextProvider>
							</PriceContextProvider>
						</PostDataContextProvider>
					</CardContextProvider>
				</SWRConfig>
			</ConfigContextProvider>
		</IntlProvider>
	);
}
